import { UserAgents } from '../Constants/UserAgents'
import { getTokenForSession } from '../API/SessionTokenClient'
import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { PlaceInAkixiNameSpace } from '../../../NonReact/utils/Utils'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { $window } from '../../../NonReact/common/AkixiWindow'

const MAX_CHECKS: number = 15
const CHECK_INTERVAL: number = 200

/**
 * A wrapper around the window.open function.
 * This is required for the Teams Native App to maintain the user's session when the external window opens.
 *
 * @param path - A string indicating the URL or path of the resource to be loaded.
 * @param target - An optional string, without whitespace, specifying the name of the browsing context the resource is being loaded into.
 * @param features - An optional string containing a comma-separated list of window features in the form.
 */
const openWindow = (path: string, target?: string, features?: string): Promise<Nullable<Window>> => {
    if ($window?.AkixiUserAgentID !== UserAgents.TEAMS && !navigator.userAgent.includes('Teams')) {
        return Promise.resolve(window.open(path, target, features))
    }

    return getTokenForSession()
        .then((authToken: Nullable<string>): Nullable<Window> => {
            const url: URLConstructor = URLConstructor.newInstance({
                baseURL: path.startsWith('http') ? path : location.origin + path
            })

            if (authToken) {
                url.addQueryParams({ authToken })
            }

            return window.open(url.constructURL(), target, features)
        })
        .catch(
            (): Nullable<Window> =>
                // we should still open a window if the get token request fails or times out
                window.open(path, target, features)
        )
}

const checkWindow = (window: Nullable<Window>): Promise<Nullable<Window>> => {
    let checkCount: number = 0

    const windowCreatedChecker = async (): Promise<Nullable<Window>> => {
        if (isVarNotEmpty(window?.document?.body)) {
            return window
        }
        if (checkCount <= MAX_CHECKS) {
            return new Promise<Nullable<Window>>((resolve): void => {
                setTimeout((): void => {
                    resolve(windowCreatedChecker())
                    checkCount += 1
                }, CHECK_INTERVAL)
            })
        }
        return null
    }

    return windowCreatedChecker()
}

/**
 * Create a new window with the correct params.
 */
export const createCustomWindow = (url: string, target: string, options?: string): Promise<Nullable<Window>> =>
    openWindow(url, target, options).then((window: Nullable<Window>): Promise<Nullable<Window>> => checkWindow(window))

PlaceInAkixiNameSpace('OpenWindow', openWindow)
